import { IconButton, Stack, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Bound } from "lib/@components/binding/Bound"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { RightDrawer } from "lib/@components/drawer/right-drawer"
import { Repeat } from "lib/@components/repeat"
import Iconify from "minimals-template/components/Iconify"
import { ScheduleTaskRow } from "./schedule-task-row"
import { ScheduleTradePicker } from "./schedule-trade-picker"

const DRAWER_SX = { width: "min(560px, calc(100vw - 48px))" }
import { shortFolderName } from "./build-assignment-board"

/**
 * One schedule's trade and its tasks' (FIQ-2457). The header sets the whole schedule's trade,
 * which makes it an exception inside an assigned folder; each task can then follow the
 * schedule or go to a different trade. Rows are kept to two lines so a long schedule's
 * tasks can be scanned without much scrolling.
 */
export function ScheduleTasksDrawer({ scheduleId, onClose }) {
    const { board, trades, readOnly, onAssignTargets, onRemoveAssignment } = useBoundContext()
    const schedule = board.schedulesById.get(scheduleId)
    const followLabel = describeFollow(board.scheduleAssignments.get(scheduleId), trades)

    return (
        // Wider than the default drawer, so each task's route can be read on one line
        <RightDrawer open={!!schedule} setOpen={handleSetOpen} onClose={onClose} sx={DRAWER_SX}>
            {/* Below the lg breakpoint RightDrawer shows its children in a dialog rendered at the
                app root, outside the board's Bound, so the task rows are given the board again */}
            <Bound
                board={board}
                trades={trades}
                readOnly={readOnly}
                onAssignTargets={onAssignTargets}
                onRemoveAssignment={onRemoveAssignment}
            >
                <Stack spacing={1} data-cy="schedule-tasks-drawer">
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack spacing={1} sx={{ minWidth: 0 }}>
                            <Typography variant="subtitle1">
                                {schedule?.code} {schedule?.title}
                            </Typography>
                            <If value={!!schedule}>
                                <ScheduleTradePicker schedule={schedule} />
                            </If>
                            <Typography variant="caption" color="text.secondary">
                                Give individual tasks a different trade. Everything else follows the schedule.
                            </Typography>
                        </Stack>
                        <IconButton
                            size="small"
                            onClick={onClose}
                            aria-label="Close"
                            data-cy="schedule-tasks-drawer-close"
                        >
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>
                    <Repeat
                        list={schedule?.tasks ?? []}
                        keyFn={taskKey}
                        pass="task"
                        item={<ScheduleTaskRow schedule={schedule} followLabel={followLabel} />}
                    />
                </Stack>
            </Bound>
        </RightDrawer>
    )

    function handleSetOpen(value) {
        if (!value) onClose()
    }
}

// The picker option that removes a task's own trade names what takes over
function describeFollow(assignment, trades) {
    if (!assignment) return "Follow skill routing"
    const trade = trades[assignment.tradeId]
    if (assignment.kind === "folder") return `Follow the ${shortFolderName(assignment.name)} folder (${trade})`
    return `Follow the schedule's assignment (${trade})`
}

function taskKey(task) {
    return task.taskId
}
